import { SubmitHandler, useForm } from 'react-hook-form';
import { DocumentInfo, documentInfoSchema } from '@interfaces/auth';
import { StepProps } from '@pages/auth/register';
import { Button } from '@vibTheme/components/_base/Button';
import {
  InputDate,
  InputExternalValidation,
  InputSelect,
} from '@vibTheme/components/_base/Input';
import { isCpf } from '@utils/inputValidation';
import { isExistsByValid } from '@interfaces/profile';
import { useExternalValidationResolver } from '@hooks/useExternalValidationResolver';
import { sendRdEventCpfBirthDate } from '@api/rdStation';
import { useCategories } from '@hooks/useCategories';
import { InputGender } from '@vibTheme/components/_base/Input/InputGender';

export function DocumentInfoStep({ stateStorage, publish }: StepProps) {
  const {
    externalValidationResolver,
    isLoading: isExternalValidationLoading,
    isValid: isExternalValidationValid,
  } = useExternalValidationResolver(documentInfoSchema, [
    { name: 'document', validate: isExistsByValid('document') },
  ]);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid, isValidating },
  } = useForm<DocumentInfo>({
    resolver: externalValidationResolver,
    mode: 'onChange',
    defaultValues: {
      categoryId: stateStorage?.categoryId || '',
      gender: stateStorage?.gender,
    },
  });

  const onSubmit: SubmitHandler<DocumentInfo> = async (data) => {
    await sendRdEventCpfBirthDate({
      email: String(stateStorage?.email),
      phone: String(stateStorage?.phone),
      category: categoryOptions[+data.categoryId - 1].label,
    }).catch(console.error);

    data.gender = data.genderFreeText || data.gender || null;
    publish({ ...stateStorage, ...data });
  };

  const document = watch('document');
  const isDocumentCpf = isCpf(document || stateStorage?.document);

  const { categories, categoryOptions } = useCategories();

  return (
    <form
      id="register-form-documentinfo"
      className="flex flex-col w-full text-left gap-main"
      onSubmit={handleSubmit(onSubmit)}
    >
      <InputExternalValidation
        control={control}
        name="document"
        label="CPF/CNPJ"
        placeholder="ex: 123.456.789-00"
        fullWidth
        error={!!errors.document}
        helperText={errors.document?.message}
        defaultValue={stateStorage?.document ?? ''}
        inputType="document"
        isLoading={isExternalValidationLoading.document}
        isValid={isExternalValidationValid.document}
      />
      {isDocumentCpf && (
        <InputDate
          id="register-input-birthdate"
          type="date"
          name="birthDate"
          control={control}
          label="Data de Nascimento"
          error={!!errors.birthDate}
          helperText={errors.birthDate?.message}
          placeholder="DD/MM/AAAA"
          defaultValue={stateStorage?.birthDate}
        />
      )}

      <InputGender
        name="gender"
        freeTextName="genderFreeText"
        control={control}
      />

      {categories.length > 0 && (
        <InputSelect
          name="categoryId"
          label="Categoria"
          control={control}
          options={categoryOptions}
          className="w-full"
          error={!!errors.categoryId}
          helperText={errors.categoryId?.message}
        />
      )}
      <Button
        id="register-button-submit-documentinfo"
        type="submit"
        disabled={!isValid || isValidating}
      >
        Próximo
      </Button>
    </form>
  );
}
